/**
 * Срок задачи.
 *
 * До этого срок существовал только в модели: API его отдавал, форма
 * создания не давала его задать, карточка у водителя не показывала,
 * а таблица в консоли печатала `24.07.26, 18:00` — дату, по которой
 * нельзя понять, это вчера или через месяц.
 *
 * Здесь срок превращается в ответ на вопрос «когда за это браться»:
 * «Просрочено на 2 дня», «Сегодня до 18:00», «Завтра». Абсолютная
 * дата остаётся в подсказке — она нужна, но реже.
 *
 * Все вычисления идут от локальной полуночи, а не от «сейчас минус
 * 24 часа»: задача со сроком сегодня в 09:00, открытая в 23:00, —
 * это «сегодня», а не «19 часов назад».
 */

const DAY_MS = 24 * 60 * 60 * 1000

/** Локальная полночь указанного дня. */
export function startOfDay(value = new Date()) {
  const d = new Date(value)
  d.setHours(0, 0, 0, 0)
  return d
}

/** Разница в календарных днях: сегодня → 0, завтра → 1, вчера → −1. */
export function daysUntil(value, now = new Date()) {
  if (!value) return null
  return Math.round((startOfDay(value) - startOfDay(now)) / DAY_MS)
}

export const DUE_BUCKETS = {
  none: { label: 'Без срока', tone: 'neutral' },
  overdue: { label: 'Просрочено', tone: 'danger' },
  today: { label: 'Сегодня', tone: 'warning' },
  tomorrow: { label: 'Завтра', tone: 'warning' },
  week: { label: 'На этой неделе', tone: 'info' },
  later: { label: 'Позже', tone: 'neutral' },
  done: { label: 'Закрыта', tone: 'success' },
}

const CLOSED = ['completed', 'cancelled']

/**
 * Корзина, в которую попадает задача.
 * Закрытая задача не бывает просроченной — срок для неё уже история.
 */
export function dueBucket(task, now = new Date()) {
  if (!task?.due_at) return CLOSED.includes(task?.status) ? 'done' : 'none'
  if (CLOSED.includes(task.status)) return 'done'

  const diff = daysUntil(task.due_at, now)
  if (new Date(task.due_at) < now) return 'overdue'
  if (diff === 0) return 'today'
  if (diff === 1) return 'tomorrow'
  if (diff <= 7) return 'week'
  return 'later'
}

/* Экспортируется: то же склонение нужно срокам обслуживания машин,
   а второй экземпляр правила разошёлся бы с первым при первой же правке. */
export function plural(n, one, few, many) {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few
  return many
}

const timeFmt = (value) =>
  new Date(value).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })

const dateFmt = (value) =>
  new Date(value).toLocaleDateString('ru', { day: 'numeric', month: 'short' })

/**
 * Человеческая формулировка срока.
 *
 * @returns {null | { label, short, tone, bucket, exact, isOverdue }}
 */
export function describeDue(task, now = new Date()) {
  if (!task?.due_at) return null

  const bucket = dueBucket(task, now)
  const exact = new Date(task.due_at).toLocaleString('ru', {
    dateStyle: 'long',
    timeStyle: 'short',
  })

  if (bucket === 'done') {
    return {
      bucket,
      tone: 'neutral',
      label: `Срок был ${dateFmt(task.due_at)}`,
      short: dateFmt(task.due_at),
      exact,
      isOverdue: false,
    }
  }

  const diff = daysUntil(task.due_at, now)

  if (bucket === 'overdue') {
    const late = Math.abs(diff)
    const label =
      late === 0
        ? `Просрочено сегодня, срок был в ${timeFmt(task.due_at)}`
        : `Просрочено на ${late} ${plural(late, 'день', 'дня', 'дней')}`
    return {
      bucket,
      tone: 'danger',
      label,
      short: late === 0 ? 'Просрочено' : `−${late} ${plural(late, 'день', 'дня', 'дней')}`,
      exact,
      isOverdue: true,
    }
  }

  if (bucket === 'today') {
    return {
      bucket,
      tone: 'warning',
      label: `Сегодня до ${timeFmt(task.due_at)}`,
      short: `Сегодня, ${timeFmt(task.due_at)}`,
      exact,
      isOverdue: false,
    }
  }

  if (bucket === 'tomorrow') {
    return {
      bucket,
      tone: 'warning',
      label: `Завтра до ${timeFmt(task.due_at)}`,
      short: `Завтра, ${timeFmt(task.due_at)}`,
      exact,
      isOverdue: false,
    }
  }

  return {
    bucket,
    tone: bucket === 'week' ? 'info' : 'neutral',
    label: `Через ${diff} ${plural(diff, 'день', 'дня', 'дней')} — ${dateFmt(task.due_at)}`,
    short: dateFmt(task.due_at),
    exact,
    isOverdue: false,
  }
}

/**
 * Пресеты для быстрой установки срока.
 *
 * Ставить срок мышью по календарю — четыре клика; «сегодня к вечеру» —
 * один. В Jira это самая используемая часть поля даты.
 */
export const DUE_PRESETS = [
  {
    key: 'today',
    label: 'Сегодня, 18:00',
    at: (now = new Date()) => {
      const d = startOfDay(now)
      d.setHours(18, 0, 0, 0)
      return d
    },
  },
  {
    key: 'tomorrow',
    label: 'Завтра, 10:00',
    at: (now = new Date()) => {
      const d = startOfDay(now)
      d.setDate(d.getDate() + 1)
      d.setHours(10, 0, 0, 0)
      return d
    },
  },
  {
    key: 'week',
    label: 'Через неделю',
    at: (now = new Date()) => {
      const d = startOfDay(now)
      d.setDate(d.getDate() + 7)
      d.setHours(18, 0, 0, 0)
      return d
    },
  },
]

/** Быстрые подборки по сроку — общие для списка, доски и календаря. */
export const TASK_DUE_FILTERS = [
  { key: '', label: 'Любой срок' },
  { key: 'overdue', label: 'Просрочены' },
  { key: 'today', label: 'Сегодня' },
  { key: 'week', label: 'Ближайшая неделя' },
  { key: 'none', label: 'Без срока' },
]

/** Отбор по ключу подборки. Пустой ключ ничего не отбрасывает. */
export function matchesDueFilter(task, key, now = new Date()) {
  if (!key) return true
  const bucket = dueBucket(task, now)
  if (key === 'week') return ['overdue', 'today', 'tomorrow', 'week'].includes(bucket)
  return bucket === key
}

// ── Преобразования для <input type="datetime-local"> ────────────────
//
// Поле работает в локальном времени и без часового пояса, сервер —
// в UTC с ним. Без явного преобразования срок уезжает на разницу
// поясов, и это тот класс ошибок, который замечают в проде.

export function toDateTimeLocal(value) {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n) => String(n).padStart(2, '0')
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  )
}

export function fromDateTimeLocal(value) {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}
